import React from 'react'

interface GridOverlayProps {
  imageUrl: string
  imageName: string
  issues?: Array<{
    rule: string
    gridLocation?: string
    coordinates?: { x: number; y: number; width: number; height: number }
  }>
  showGrid?: boolean
}

export default function GridOverlay({ 
  imageUrl, 
  imageName, 
  issues = [],
  showGrid = true 
}: GridOverlayProps) {
  const gridColumns = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']
  const gridRows = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']

  return (
    <div className="relative inline-block">
      {/* Base Image */}
      <img 
        src={imageUrl} 
        alt={imageName}
        className="w-full h-auto max-h-96 object-contain"
      />
      
      {/* Grid Overlay */}
      {showGrid && (
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {/* Vertical Grid Lines */}
          {Array.from({ length: 13 }, (_, i) => (
            <line
              key={`v-${i}`}
              x1={i * (100 / 12)}
              y1="0"
              x2={i * (100 / 12)}
              y2="100"
              stroke="rgba(59, 130, 246, 0.3)"
              strokeWidth="0.2"
            />
          ))}
          
          {/* Horizontal Grid Lines */}
          {Array.from({ length: 13 }, (_, i) => (
            <line
              key={`h-${i}`}
              x1="0"
              y1={i * (100 / 12)}
              x2="100"
              y2={i * (100 / 12)}
              stroke="rgba(59, 130, 246, 0.3)"
              strokeWidth="0.2"
            />
          ))}
          
          {/* Column Labels (A-L) */}
          {gridColumns.map((col, i) => (
            <text
              key={`col-${col}`}
              x={i * (100 / 12) + (100 / 24)}
              y="3"
              fontSize="2.5"
              fill="rgba(59, 130, 246, 0.7)"
              textAnchor="middle"
              fontWeight="bold"
            >
              {col}
            </text>
          ))}
          
          {/* Row Labels (1-12) */}
          {gridRows.map((row, i) => (
            <text
              key={`row-${row}`}
              x="2"
              y={i * (100 / 12) + (100 / 24) + 1}
              fontSize="2.5"
              fill="rgba(59, 130, 246, 0.7)"
              textAnchor="start"
              fontWeight="bold"
            >
              {row}
            </text>
          ))}
        </svg>
      )}
      
      {/* Issue Markers */}
      {issues.length > 0 && (
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {issues.map((issue, index) => {
            if (!issue.coordinates) return null
            
            const { x, y, width, height } = issue.coordinates
            const color = issue.rule.toLowerCase().includes('fail') ? 'rgb(239, 68, 68)' : 'rgb(234, 179, 8)'
            
            return (
              <g key={index}>
                {/* Bounding Box */}
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill="none"
                  stroke={color}
                  strokeWidth="0.8"
                  strokeDasharray="2,1"
                />
                
                {/* Corner Markers */}
                <circle cx={x} cy={y} r="1" fill={color} />
                <circle cx={x + width} cy={y} r="1" fill={color} />
                <circle cx={x} cy={y + height} r="1" fill={color} />
                <circle cx={x + width} cy={y + height} r="1" fill={color} />
                
                {/* Label */}
                <text
                  x={x + width / 2}
                  y={y - 1}
                  fontSize="2"
                  fill={color}
                  textAnchor="middle"
                  fontWeight="bold"
                >
                  {issue.gridLocation || `Issue ${index + 1}`}
                </text>
              </g>
            )
          })}
        </svg>
      )}
      
      {/* Grid Reference Legend */}
      {showGrid && (
        <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-[10px] font-mono">
          Grid: A-L × 1-12
        </div>
      )}
    </div>
  )
}


